import React from 'react'
import { motion } from 'motion/react'

import type { HgChatItem } from '../../api/client'
import ChatMarkdown from './ChatMarkdown'
import CheckinEventCard from './CheckinEventCard'
import { PersonaAvatar } from './PersonaPicker'
import ProposalCard, { proposalState } from './ProposalCard'

export const UserBubble: React.FC<{ text: string; pending?: boolean }> = ({ text, pending }) => (
    <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ type: 'spring', stiffness: 500, damping: 30 }}
        style={{ transformOrigin: 'bottom right' }}
        className="mb-4 flex justify-end"
        data-testid="chat-user-message"
    >
        <div
            className={`max-w-[85%] p-3 rounded-lg rounded-br-none bg-action text-plum text-base whitespace-pre-wrap break-words ${
                pending ? 'opacity-70' : ''
            }`}
        >
            {text}
        </div>
    </motion.div>
)

/** Who is speaking, above what they said. */
const SpeakerLine: React.FC<{ name: string; imageUrl?: string | null; testId: string }> = ({
    name,
    imageUrl,
    testId,
}) => (
    <div className="mb-1 flex items-center gap-2 text-sm font-medium text-gray-700">
        <PersonaAvatar name={name} imageUrl={imageUrl} />
        <span data-testid={testId}>{name}</span>
    </div>
)

/** Honey's reply is the page's main text: no box, no name. Advisors and
 *  the user are the ones set apart. */
export const AssistantMessage: React.FC<{
    text: string
    streaming?: boolean
    interrupted?: boolean
}> = ({ text, streaming, interrupted }) => (
    <div className="mb-6 text-gray-800" data-testid={streaming ? 'chat-streaming-message' : 'chat-assistant-message'}>
        <ChatMarkdown text={text} streaming={streaming} />
        {streaming && <span className="inline-block animate-pulse ml-1 text-gray-500">▋</span>}
        {interrupted && <div className="mt-1 text-sm text-gray-500">Stopped</div>}
    </div>
)

/** An advisor speaking: their picture and name above what they said. */
export const PersonaBubble: React.FC<{
    name: string
    imageUrl?: string | null
    text: string
    streaming?: boolean
    interrupted?: boolean
}> = ({ name, imageUrl, text, streaming, interrupted }) => (
    <div className="mb-4" data-testid={streaming ? 'chat-streaming-message' : 'chat-persona-message'}>
        <SpeakerLine name={name} imageUrl={imageUrl} testId="chat-persona-name" />
        <div className="p-3 rounded-lg bg-white/80 text-gray-800">
            <ChatMarkdown text={text} streaming={streaming} />
            {streaming && <span className="inline-block animate-pulse ml-1 text-gray-500">▋</span>}
        </div>
        {interrupted && <div className="mt-1 text-sm text-gray-500">Stopped</div>}
    </div>
)

/** A line from the chat itself rather than from either side of it. */
export const ChatNote: React.FC<{ children: React.ReactNode; tone?: 'quiet' | 'error' }> = ({
    children,
    tone = 'quiet',
}) => (
    <div
        className={`mb-4 text-sm ${tone === 'error' ? 'text-rose-600' : 'text-gray-500'}`}
        data-testid="chat-note"
    >
        {children}
    </div>
)

/** What a compaction left in place of the messages it replaced, as Honey's
 *  text under a quiet line saying so. */
const CompactionSummary: React.FC<{ text: string }> = ({ text }) => (
    <div data-testid="chat-compaction-summary">
        <ChatNote>Summary of earlier messages</ChatNote>
        <div className="mb-6 text-gray-800">
            <ChatMarkdown text={text} />
        </div>
    </div>
)

const NOTICE_TEXT: Record<string, string> = {
    step_cap: 'Stopped: this was taking too long.',
    step_limit: 'That reply ran too long and was stopped.',
}

const CHECKIN_NOTICES = ['checkin_created', 'checkin_updated']

/** The seq of the latest `checkin_parsed` notice for this check-in after its
 *  card's notice: the card reads the check-in again when it changes. The
 *  parsed notice draws nothing of its own. */
export const latestParsedSeq = (checkinId: number, afterSeq: number, items: HgChatItem[]): number | null => {
    let latest: number | null = null
    for (const i of items) {
        if (
            i.seq > afterSeq &&
            i.item_type === 'notice' &&
            i.content?.kind === 'checkin_parsed' &&
            Number(i.content?.data?.checkin_id) === checkinId
        ) {
            latest = i.seq
        }
    }
    return latest
}

export type DecideProposal = (
    proposalId: number,
    decision: 'approve' | 'reject',
    subset?: string[],
) => Promise<boolean>

/** One visible item. Item types this page has nothing to show for render
 *  nothing rather than a placeholder — an `approval` among them, which shows
 *  as its proposal card's state. A proposal's state is read from the items
 *  after it, so the card needs the transcript. */
const ChatItemView: React.FC<{
    item: HgChatItem
    items?: HgChatItem[]
    onDecide?: DecideProposal
}> = ({ item, items = [], onDecide }) => {
    const content = item.content ?? {}
    switch (item.item_type) {
        case 'user_message':
            return <UserBubble text={String(content.text ?? '')} />
        case 'assistant_message': {
            const text = String(content.text ?? '')
            if (!text && !content.interrupted) return null
            if (!text) return <ChatNote>Stopped</ChatNote>
            return <AssistantMessage text={text} interrupted={Boolean(content.interrupted)} />
        }
        case 'persona_message': {
            const text = String(content.text ?? '')
            if (!text && !content.interrupted) return null
            if (!text) return <ChatNote>Stopped</ChatNote>
            return (
                <PersonaBubble
                    name={String(content.persona_name ?? 'Advisor')}
                    imageUrl={content.persona_image_url ?? null}
                    text={text}
                    interrupted={Boolean(content.interrupted)}
                />
            )
        }
        case 'proposal': {
            const proposalId = Number(content.proposal_id ?? item.proposal_id)
            const later = items.filter((i) => i.seq > item.seq)
            return (
                <ProposalCard
                    item={item}
                    state={proposalState(proposalId, later)}
                    onDecide={onDecide ?? (async () => false)}
                />
            )
        }
        case 'notice': {
            if (CHECKIN_NOTICES.includes(String(content.kind))) {
                const data = content.data ?? {}
                const checkinId = Number(data.checkin_id)
                if (!checkinId) return null
                return (
                    <CheckinEventCard
                        checkinId={checkinId}
                        parseJobId={data.parse_job_id ?? null}
                        parsedSeq={latestParsedSeq(checkinId, item.seq, items)}
                    />
                )
            }
            const line = NOTICE_TEXT[String(content.kind ?? '')]
            return line ? <ChatNote>{line}</ChatNote> : null
        }
        case 'compaction_summary': {
            const text = String(content.text ?? '')
            return text ? <CompactionSummary text={text} /> : null
        }
        default:
            return null
    }
}

export default ChatItemView
